import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 for unauthenticated visitors is expected; swallow but log other errors.
      if (err?.response?.status && err.response.status !== 401) {
        console.error("AuthContext /me failed:", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check while OAuth callback is processing the URL fragment.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout failed:", err);
    }
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.put("/auth/profile", payload);
    setUser(data);
    return data;
  }, []);

  const uploadAvatar = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/auth/avatar", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setUser(data);
    return data;
  }, []);

  const removeAvatar = useCallback(async () => {
    const { data } = await api.delete("/auth/avatar");
    setUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, updateProfile, uploadAvatar, removeAvatar, checkAuth }),
    [user, loading, login, register, logout, updateProfile, uploadAvatar, removeAvatar, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
